import React, { useState, useEffect, useCallback } from 'react';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiTarget,
  FiClock,
  FiBarChart2,
  FiPieChart,
  FiFilter
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { analyticsAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const Performance = () => {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    timeRange: '30d',
    subject: 'all',
    grade: 'all',
    testType: 'all'
  });
  const { showError } = useNotification();

  const subjects = ['Mathematics', 'Science', 'English', 'History', 'Geography', 'Physics', 'Chemistry', 'Biology', 'Computer Science'];
  const grades = ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10'];

  const fetchPerformanceData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await analyticsAPI.getPerformanceAnalytics(filters);
      setPerformanceData(response.data);
    } catch (error) {
      showError('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  }, [filters, showError]);

  useEffect(() => {
    fetchPerformanceData();
  }, [fetchPerformanceData]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const formatPercentage = (value) => `${(value || 0).toFixed(1)}%`;

  const formatTime = (minutes) => {
    if (minutes == null) return '—';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getPerformanceLevel = (score) => {
    if (score >= 90) return { level: 'Excellent', className: 'bg-emerald-100 text-emerald-800' };
    if (score >= 80) return { level: 'Good', className: 'bg-blue-100 text-blue-800' };
    if (score >= 70) return { level: 'Average', className: 'bg-slate-100 text-slate-700' };
    if (score >= 60) return { level: 'Below Average', className: 'bg-amber-100 text-amber-800' };
    return { level: 'Poor', className: 'bg-red-100 text-red-800' };
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return <FiTrendingUp className="h-4 w-4 text-emerald-500 inline-block ml-1" />;
    if (trend < 0) return <FiTrendingDown className="h-4 w-4 text-red-500 inline-block ml-1" />;
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Performance Analysis</h1>
            <p className="text-sm text-slate-500 mt-0.5">Detailed breakdown of learning performance.</p>
          </div>
          <button type="button" className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
            Export Report
          </button>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <FiFilter className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-medium text-slate-900">Filters</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Time Range</label>
              <select value={filters.timeRange} onChange={(e) => handleFilterChange('timeRange', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 3 months</option>
                <option value="1y">Last year</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Subject</label>
              <select value={filters.subject} onChange={(e) => handleFilterChange('subject', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                <option value="all">All Subjects</option>
                {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Grade</label>
              <select value={filters.grade} onChange={(e) => handleFilterChange('grade', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                <option value="all">All Grades</option>
                {grades.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Test Type</label>
              <select value={filters.testType} onChange={(e) => handleFilterChange('testType', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                <option value="all">All Types</option>
                <option value="practice">Practice Tests</option>
                <option value="assessment">Assessments</option>
                <option value="game">Games</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h3 className="text-sm font-medium text-slate-900 mb-3">Overall Performance</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-2 text-slate-600 mb-2">
                <FiTarget className="h-4 w-4" />
                <span className="text-sm font-medium">Average Score</span>
              </div>
              <div className="text-xl font-semibold text-slate-900">
                {formatPercentage(performanceData?.averageScore)}
                {getTrendIcon(performanceData?.scoreTrend)}
              </div>
              <div className="mt-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPerformanceLevel(performanceData?.averageScore).className}`}>
                  {getPerformanceLevel(performanceData?.averageScore).level}
                </span>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-2 text-slate-600 mb-2">
                <FiClock className="h-4 w-4" />
                <span className="text-sm font-medium">Avg. Completion Time</span>
              </div>
              <div className="text-xl font-semibold text-slate-900">
                {formatTime(performanceData?.averageTime)}
                {getTrendIcon(performanceData?.timeTrend)}
              </div>
              <div className="text-xs text-slate-500 mt-1">Target: {formatTime(performanceData?.targetTime)}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-2 text-slate-600 mb-2">
                <FiBarChart2 className="h-4 w-4" />
                <span className="text-sm font-medium">Accuracy Rate</span>
              </div>
              <div className="text-xl font-semibold text-slate-900">
                {formatPercentage(performanceData?.accuracyRate)}
                {getTrendIcon(performanceData?.accuracyTrend)}
              </div>
              <div className="text-xs text-slate-500 mt-1">{performanceData?.totalQuestions ?? 0} questions answered</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-2 text-slate-600 mb-2">
                <FiPieChart className="h-4 w-4" />
                <span className="text-sm font-medium">Improvement Rate</span>
              </div>
              <div className="text-xl font-semibold text-slate-900">
                {formatPercentage(performanceData?.improvementRate)}
                {getTrendIcon(performanceData?.improvementTrend)}
              </div>
              <div className="text-xs text-slate-500 mt-1">vs. previous period</div>
            </div>
          </div>
        </div>

        {performanceData?.subjectPerformance && performanceData.subjectPerformance.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-medium text-slate-900 mb-3">Performance by Subject</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {performanceData.subjectPerformance.map((subject, index) => {
                const perf = getPerformanceLevel(subject.averageScore);
                return (
                  <div key={index} className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-slate-900">{subject.name}</h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${perf.className}`}>{perf.level}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div><span className="text-slate-500">Avg Score</span><div className="font-medium text-slate-900">{formatPercentage(subject.averageScore)}</div></div>
                      <div><span className="text-slate-500">Tests</span><div className="font-medium text-slate-900">{subject.testsTaken ?? 0}</div></div>
                      <div><span className="text-slate-500">Time Spent</span><div className="font-medium text-slate-900">{formatTime(subject.timeSpent)}</div></div>
                      <div><span className="text-slate-500">Improvement</span><div className={`font-medium ${subject.improvement >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{subject.improvement >= 0 ? '+' : ''}{formatPercentage(subject.improvement)}</div></div>
                    </div>
                    <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                      <div className="h-full rounded-full bg-slate-600" style={{ width: `${Math.min(100, subject.averageScore || 0)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {performanceData?.difficultyAnalysis && performanceData.difficultyAnalysis.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-medium text-slate-900 mb-3">Performance by Difficulty Level</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {performanceData.difficultyAnalysis.map((level, index) => (
                <div key={index} className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-slate-900">{level.level}</h4>
                    <span className="text-xs text-slate-500">{level.questionCount ?? 0} questions</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-slate-500">Accuracy</span>
                    <span className="font-medium text-slate-900">{formatPercentage(level.accuracy)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mb-3">
                    <span className="text-slate-500">Avg. Time</span>
                    <span className="font-medium text-slate-900">{formatTime(level.averageTime)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                    <div className="h-full rounded-full bg-slate-600" style={{ width: `${Math.min(100, level.accuracy || 0)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {performanceData?.recommendations && performanceData.recommendations.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-slate-900 mb-3">Performance Recommendations</h3>
            <div className="space-y-3">
              {performanceData.recommendations.map((rec, index) => (
                <div key={index} className={`rounded-lg border border-slate-200 bg-white shadow-sm p-4 flex items-start gap-3 ${rec.priority === 'high' ? 'border-amber-200 bg-amber-50/30' : ''}`}>
                  <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                    {rec.type === 'improvement' && <FiTrendingUp className="h-4 w-4" />}
                    {rec.type === 'focus' && <FiTarget className="h-4 w-4" />}
                    {rec.type === 'practice' && <FiBarChart2 className="h-4 w-4" />}
                    {!['improvement', 'focus', 'practice'].includes(rec.type) && <FiTarget className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-slate-900">{rec.title}</h4>
                    <p className="text-sm text-slate-600 mt-0.5">{rec.description}</p>
                    {rec.action && (
                      <button type="button" className="mt-2 inline-flex items-center px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50">
                        {rec.action}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Performance;
